package com.xorrus.trading.lot.remote.connect

import io.kvision.html.Div
import io.kvision.snabbdom.VNode
import kotlinx.browser.document
import kotlinx.browser.window
import org.khronos.webgl.Uint8ClampedArray
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLVideoElement

/** jsQR (npm): dekodiert ein RGBA-Bild; null, wenn kein QR-Code gefunden wurde. */
@JsModule("jsqr")
@JsNonModule
internal external fun jsQR(data: Uint8ClampedArray, width: Int, height: Int): JsQrCode?

internal external interface JsQrCode {
    val data: String
}

/**
 * Kamera-QR-Scanner: rückwärtige Kamera per getUserMedia, Frames auf ein
 * Offscreen-Canvas, Dekodierung mit jsQR im ~250-ms-Takt. [onDecoded] feuert
 * genau einmal; danach stoppt der Scanner. Kamera braucht einen Secure Context
 * (https bzw. localhost).
 */
class QrScannerPanel(
    private val onDecoded: (String) -> Unit,
    private val onError: (String) -> Unit,
) : Div(className = "qr-scanner ratio ratio-1x1 rounded overflow-hidden bg-black") {

    private var video: HTMLVideoElement? = null
    private var stream: dynamic = null
    private var timerId: Int? = null
    private var done = false

    init {
        // Eigener Snabbdom-Key: Kamera-Start und -Stopp hängen an den Insert-/Destroy-Hooks
        // des DOM-Elements. Ohne Key verwendet der virtuelle DOM beim Screen-Wechsel ein an
        // gleicher Position stehendes fremdes <div> wieder (z. B. die Desktop-Liste) — dann
        // feuert kein Insert-Hook und es bleibt nur die schwarze Fläche ohne Video. Umgekehrt
        // liefe die Kamera weiter, wenn dieses <div> beim Verlassen nur umgewidmet statt
        // entfernt würde. Mit Key wird das Element immer frisch eingefügt und wirklich entfernt.
        useSnabbdomDistinctKey()
    }

    override fun afterInsert(node: VNode) {
        super.afterInsert(node)
        val host = getElement() ?: return

        val v = document.createElement("video") as HTMLVideoElement
        v.setAttribute("playsinline", "") // iOS: nicht in den Vollbild-Player springen
        v.setAttribute("muted", "")
        v.style.width = "100%"
        v.style.height = "100%"
        v.style.setProperty("object-fit", "cover")
        host.appendChild(v)
        video = v

        val mediaDevices = window.navigator.asDynamic().mediaDevices
        if (mediaDevices == null || mediaDevices == undefined) {
            onError(S.cameraUnavailableHttps)
            return
        }
        mediaDevices.getUserMedia(js("""{ video: { facingMode: "environment" }, audio: false }"""))
            .then { s: dynamic ->
                stream = s
                v.asDynamic().srcObject = s
                v.play()
                startLoop(v)
            }
            .catch { _: dynamic ->
                onError(S.cameraAccessDenied)
            }
    }

    private fun startLoop(v: HTMLVideoElement) {
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
        timerId = window.setInterval({
            if (!done && v.videoWidth > 0 && v.videoHeight > 0) {
                canvas.width = v.videoWidth
                canvas.height = v.videoHeight
                ctx.drawImage(v, 0.0, 0.0)
                val image = ctx.getImageData(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                val code = jsQR(image.data, image.width, image.height)
                if (code != null && code.data.isNotBlank()) {
                    done = true
                    stop()
                    onDecoded(code.data)
                }
            }
        }, 250)
    }

    private fun stop() {
        timerId?.let { window.clearInterval(it) }
        timerId = null
        val s = stream
        if (s != null) {
            val tracks = s.getTracks() as Array<dynamic>
            tracks.forEach { it.stop() }
        }
        stream = null
        video?.asDynamic()?.srcObject = null
    }

    override fun afterDestroy() {
        stop()
        super.afterDestroy()
    }
}
